import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

import { TextField, TextareaField, SelectField } from "./Fields";
import Honeypot from "./Honeypot";
import Turnstile from "./Turnstile";
import { contactSchema, SUBJECTS } from "../../lib/schemas";
import { submitContactLead } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";

export default function ContactForm() {
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: { _hp_check: "", subject: "General Inquiry" },
  });

  const onSubmit = async (values) => {
    setServerError(null);
    const res = await submitContactLead({
      ...values,
      turnstile_token: turnstileToken || undefined,
    });
    if (!res.ok) {
      setServerError(res.message || "Something went wrong. Please try again.");
      return;
    }
    trackEvent("generate_lead", {
      lead_type: "contact",
      service_type: values.subject,
      value: 0,
    });
    setSuccess({ ...values, lead_id: res.lead_id });
    reset();
  };

  if (success) {
    return (
      <div
        className="rounded-md border border-ridge-border bg-white p-8 md:p-10"
        role="status"
        aria-live="polite"
        data-testid="contact-success"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-ridge-amber/15 text-ridge-amber">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
          Message received.
        </h3>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-ridge-text md:text-base">
          Thanks, <strong>{success.name}</strong>. We'll reply within one business hour
          during operating hours (Mon–Fri 7am–7pm EST). A confirmation is on its way to{" "}
          <strong>{success.email}</strong>.
        </p>
        <button
          type="button"
          onClick={() => setSuccess(null)}
          className="btn-ghost-amber mt-7"
          data-testid="contact-submit-another"
        >
          Send another message
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="rounded-md border border-ridge-border bg-white p-6 md:p-8"
      aria-label="Contact form"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <TextField
          name="name"
          label="Name"
          required
          autoComplete="name"
          register={register}
          error={errors.name?.message}
          testId="contact-name"
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          required
          autoComplete="email"
          register={register}
          error={errors.email?.message}
          testId="contact-email"
        />
        <TextField
          name="phone"
          label="Phone"
          type="tel"
          hint="Optional"
          autoComplete="tel"
          register={register}
          error={errors.phone?.message}
          testId="contact-phone"
        />
        <TextField
          name="company"
          label="Company"
          hint="Optional"
          autoComplete="organization"
          register={register}
          error={errors.company?.message}
          testId="contact-company"
        />
        <div className="sm:col-span-2">
          <SelectField
            name="subject"
            label="Subject"
            required
            options={SUBJECTS}
            defaultValue="General Inquiry"
            register={register}
            error={errors.subject?.message}
            testId="contact-subject"
          />
        </div>
        <div className="sm:col-span-2">
          <TextareaField
            name="message"
            label="Message"
            required
            placeholder="Tell us a bit about what you need…"
            maxLength={2000}
            rows={5}
            register={register}
            error={errors.message?.message}
            testId="contact-message"
          />
        </div>
      </div>

      <Honeypot register={register} />
      <Turnstile onToken={setTurnstileToken} />

      {serverError ? (
        <p
          className="mt-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          role="alert"
          data-testid="contact-server-error"
        >
          {serverError}
        </p>
      ) : null}

      <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ridge-muted">
          Required fields marked with <span className="text-ridge-amber">*</span>.
        </p>
        <button
          type="submit"
          className="btn-accent w-full sm:w-auto"
          disabled={isSubmitting}
          data-testid="contact-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Sending…
            </>
          ) : (
            <>
              Send Message
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
